'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import ClientRegisterForm from './ClientRegisterForm';
import HealerRegisterForm from './HealerRegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { Heart, Shield, Star, Users } from 'lucide-react';

type AuthMode = 'login' | 'register-client' | 'register-healer';

export default function AuthPageClient() {
  const [mode, setMode] = useState<AuthMode>('login');

  const brandStats = [
    { icon: <Users size={16} />, value: '2,800+', label: 'Clients Healed' },
    { icon: <Heart size={16} />, value: '180+', label: 'Expert Healers' },
    { icon: <Star size={16} />, value: '4.9', label: 'Avg Rating' },
    { icon: <Shield size={16} />, value: '100%', label: 'Private Sessions' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] gradient-healing flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background blob */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={44} />
          <div>
            <p className="font-bold text-xl text-white tracking-tight">Healing52</p>
            <p className="text-xs text-white/70 font-medium">Relationship Healing</p>
          </div>
        </div>

        {/* Hero Text */}
        <div className="relative z-10">
          <h1 className="text-display text-white leading-tight mb-4">
            Heal Your<br />
            Relationships.<br />
            <span className="text-accent">Transform Your Life.</span>
          </h1>
          <p className="text-white/80 text-base leading-relaxed mb-8 max-w-sm">
            Connect with trusted relationship-healing professionals for private, personalized sessions designed to restore peace, love, and connection.
          </p>

          {/* Tagline */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-4 mb-8">
            <p className="text-white/90 text-sm font-medium italic leading-relaxed">
              "Heal Relationship First before Healing anything else."
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            {brandStats.map((stat) => (
              <div key={`brand-stat-${stat.label}`} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 text-white/70 mb-1">
                  {stat.icon}
                  <span className="text-[11px] font-medium uppercase tracking-wide">{stat.label}</span>
                </div>
                <p className="text-xl font-bold text-white font-tabular">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-white/50 text-xs leading-relaxed relative z-10">
          Healing52 provides complementary wellness services. Sessions are not a substitute for emergency, medical, psychiatric, or legal services.
        </p>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-thin">
        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-2.5 p-6 pb-0">
          <AppLogo size={32} />
          <div>
            <p className="font-bold text-base text-foreground">Healing52</p>
            <p className="text-[10px] text-muted-foreground">Relationship Healing</p>
          </div>
        </div>

        <div className="flex-1 flex items-start justify-center px-6 py-8 lg:px-12 xl:px-16">
          <div className="w-full max-w-md">
            {/* Mode Tabs */}
            <div className="flex gap-1 bg-muted p-1 rounded-xl mb-8">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === 'login' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('register-client')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === 'register-client' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Client
              </button>
              <button
                onClick={() => setMode('register-healer')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === 'register-healer' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Healer
              </button>
            </div>

            {/* Forms */}
            {mode === 'login' && <LoginForm onSwitchToRegister={() => setMode('register-client')} />}
            {mode === 'register-client' && <ClientRegisterForm onSwitchToLogin={() => setMode('login')} />}
            {mode === 'register-healer' && <HealerRegisterForm onSwitchToLogin={() => setMode('login')} />}

            {/* Footer */}
            <p className="text-center text-xs text-muted-foreground mt-6">
              By continuing, you agree to our{' '}
              <Link href="#" className="text-primary hover:underline">Terms</Link>
              {' '}and{' '}
              <Link href="#" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}